import { toast as sonnerToast } from 'sonner';
import type { CSSProperties } from 'react';

/**
 * Toast notification wrapper with comprehensive logging
 * Every toast call is logged to console for debugging
 * 
 * Usage:
 *   toast.success('Check-in created!')
 *   toast.error('Failed to save')
 *   toast.info('Loading...')
 *   toast.warning('Large file size')
 */

type ToastType = 'success' | 'error' | 'info' | 'warning';

const BASE_CLASS = 'ember-toast';
const BASE_DURATION = 4200;

const BASE_STYLE: CSSProperties = {
  background: '#101010',
  border: '1px solid rgba(255, 255, 255, 0.04)',
  color: '#f5f5f5',
  fontSize: '0.92rem',
  fontWeight: 500,
  fontFamily: 'Inter, sans-serif',
  padding: '0.9rem 1rem',
  borderRadius: '12px',
  boxShadow: '0 18px 32px rgba(0, 0, 0, 0.55)',
  display: 'flex',
  alignItems: 'center',
  gap: '0.65rem',
  letterSpacing: '0.01em',
};

const TYPE_DECOR: Record<ToastType, { className: string; icon: string; style: CSSProperties }> = {
  success: {
    className: `${BASE_CLASS}--success`,
    icon: '✔',
    style: {
      boxShadow: '0 18px 32px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(196, 255, 0, 0.65)',
      color: '#f2ffe0',
    },
  },
  error: {
    className: `${BASE_CLASS}--error`,
    icon: '✖',
    style: {
      boxShadow: '0 18px 32px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(255, 68, 68, 0.7)',
      color: '#ffe0e0',
    },
  },
  info: {
    className: `${BASE_CLASS}--info`,
    icon: 'ℹ',
    style: {
      boxShadow: '0 18px 32px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(96, 165, 250, 0.65)',
      color: '#e4f1ff',
    },
  },
  warning: {
    className: `${BASE_CLASS}--warning`,
    icon: '⚠',
    style: {
      boxShadow: '0 18px 32px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(250, 204, 21, 0.65)',
      color: '#fff2ce',
    },
  },
};

function buildOptions(type: ToastType, options?: any) {
  const typeDecor = TYPE_DECOR[type];
  const mergedStyle: CSSProperties = {
    ...BASE_STYLE,
    ...(typeDecor?.style || {}),
    ...(options?.style || {}),
  };

  const mergedClassName = [BASE_CLASS, typeDecor?.className, options?.className]
    .filter(Boolean)
    .join(' ');

  return {
    ...options,
    duration: options?.duration ?? BASE_DURATION,
    className: mergedClassName,
    icon: options?.icon ?? typeDecor?.icon,
    style: mergedStyle,
  };
}

const createToastWithLogging = (type: ToastType) => {
  return (message: string, options?: any) => {
    const timestamp = new Date().toISOString();
    
    // Log to console for debugging
    console.log(`[TOAST ${type.toUpperCase()}] ${timestamp}:`, message, options || '');
    
    const mergedOptions = buildOptions(type, options);

    // Show toast notification
    try {
      switch (type) {
        case 'success':
          return sonnerToast.success(message, mergedOptions);
        case 'error':
          return sonnerToast.error(message, mergedOptions);
        case 'info':
          return sonnerToast.info(message, mergedOptions);
        case 'warning':
          return sonnerToast.warning(message, mergedOptions);
        default:
          return sonnerToast(message, mergedOptions);
      }
    } catch (error) {
      // Fallback to console if toast fails
      console.error('[TOAST ERROR] Failed to show toast, logging instead:', error);
      console.log(`[FALLBACK ${type.toUpperCase()}]:`, message);
      
      // Ultimate fallback to alert for critical errors
      if (type === 'error') {
        alert(message);
      }
    }
  };
};

export const toast = {
  success: createToastWithLogging('success'),
  error: createToastWithLogging('error'),
  info: createToastWithLogging('info'),
  warning: createToastWithLogging('warning'),
  
  // Promise-based toast for async operations
  promise: (promise: Promise<any>, messages: { loading: string; success: string; error: string }) => {
    console.log('[TOAST PROMISE] Started:', messages.loading);
    return sonnerToast.promise(promise, messages);
  },
};

